import { useQuery, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, getCurrentOrgId } from "@/lib/api/client";
import { ApiError } from "@/lib/api/errors";
import { apiKey, apiQuery, useApiQuery } from "@/lib/api/hooks";
import { usePrefs } from "@/lib/prefs";
import type { ChannelAccount, Me, Member } from "@/lib/types";

/** Shared reads used across screens (cached once, kept fresh by SSE invalidation). */
export const meQuery = () => ({ ...apiQuery("me"), staleTime: 5 * 60_000 });

/**
 * QA F10: "am I signed in?" without a 401 in the console. Asks GET /v1/auth/session (always 200) and caches `me`;
 * signed out → the same 401 ApiError the guards already handle, built locally (no failed request).
 */
export async function probeSession(qc: QueryClient): Promise<Me> {
  const s = await api("session");
  if (!s.authenticated || !s.me) throw new ApiError({ status: 401, route: "session", problem: { type: "about:blank", title: "unauthenticated", status: 401 } });
  qc.setQueryData(apiKey("me"), s.me);
  return s.me;
}

/** The cached `me`, else the session probe (route guards). */
export async function ensureMe(qc: QueryClient): Promise<Me> {
  return qc.getQueryData<Me>(apiKey("me")) ?? probeSession(qc);
}

export function useMe() {
  return useQuery(meQuery());
}

export function useOrg() {
  const { data } = useMe();
  return data?.orgs.find((o) => o.id === (getCurrentOrgId() ?? data.currentOrgId)) ?? data?.orgs[0] ?? null;
}

export function useAccounts() {
  return useApiQuery("accounts", {}, { staleTime: 60_000 });
}

/** The account the dashboard/analytics focus on (null = all), validated against the list. */
export function useSelectedAccount(): { accountId: string | null; account: ChannelAccount | null; accounts: ChannelAccount[] } {
  const { accountId } = usePrefs();
  const { data: accounts = [] } = useAccounts();
  return useMemo(() => {
    const account = accounts.find((a) => a.id === accountId) ?? null;
    return { accountId: account?.id ?? null, account, accounts };
  }, [accounts, accountId]);
}

export function useMembers() {
  return useApiQuery("members", {}, { staleTime: 5 * 60_000 });
}

export function useMemberMap(): Map<string, Member> {
  const { data } = useMembers();
  return useMemo(() => new Map((data ?? []).map((m) => [m.user.id, m])), [data]);
}

export function useInboxCounts() {
  return useApiQuery("inboxCounts", {}, { refetchInterval: 60_000 });
}
